// shared/schema/workos.ts
//
// Pack 11.A — WorkOS Enterprise Authentication Platform (Task #1115).
//
// Drizzle schema for the WorkOS SCIM event idempotency log. The table is
// the canonical "have we already processed this WorkOS event?" surface —
// every webhook handler does an INSERT ON CONFLICT DO NOTHING keyed on
// `workosEventId` so retries from WorkOS are no-ops without re-running the
// expensive provisioning logic.

import { sql } from "drizzle-orm";
import { pgTable, varchar, text, jsonb, timestamp, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

export const workosScimEvents = pgTable(
  "workos_scim_events",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    workosEventId: text("workos_event_id").notNull().unique(),
    eventType: text("event_type").notNull(),
    workosOrgId: text("workos_org_id"),
    workosDirectoryId: text("workos_directory_id"),
    workosDirectoryUserId: text("workos_directory_user_id"),
    workosDirectoryGroupId: text("workos_directory_group_id"),
    tenantId: text("tenant_id"),
    /** 'processed' | 'unhandled' | 'error'. */
    status: text("status").notNull().default("processed"),
    errorMessage: text("error_message"),
    payload: jsonb("payload").$type<Record<string, unknown>>().notNull(),
    receivedAt: timestamp("received_at", { withTimezone: true }).notNull().defaultNow(),
    processedAt: timestamp("processed_at", { withTimezone: true }),
  },
  (t) => ({
    idxWorkosScimEventsTenant: index("idx_workos_scim_events_tenant").on(t.tenantId, t.receivedAt),
    idxWorkosScimEventsDirectory: index("idx_workos_scim_events_directory").on(t.workosDirectoryId, t.receivedAt),
    idxWorkosScimEventsStatus: index("idx_workos_scim_events_status").on(t.status, t.receivedAt),
  }),
);

export const insertWorkosScimEventSchema = createInsertSchema(workosScimEvents).omit({
  id: true,
  receivedAt: true,
  processedAt: true,
});
export type InsertWorkosScimEvent = z.infer<typeof insertWorkosScimEventSchema>;
export type WorkosScimEvent = typeof workosScimEvents.$inferSelect;
